import { jsx } from "react/jsx-runtime";
import React__default, { useRef, useState, useEffect, useCallback } from "react";
import { useJsApiLoader, GoogleMap, Marker } from "@react-google-maps/api";
import { MAP_CENTER } from "../Map.constants.js";
const MIN_ZOOM = 3;
const DEFAULT_PLACEMARK_SIZE = [60, 72];
const DEFAULT_PLACEMARK_HOVER_SIZE = [82, 82];
const DEFAULT_PLACEMARK_ACTIVE_SIZE = [92, 92];
const containerStyle = {
  width: "100%",
  height: "100%"
};
const Google = ({
  onSelectPin,
  onMapClick,
  mapsKey,
  selected,
  placemarkImage,
  placemarkActiveImage,
  placemarkHoverImage,
  pins,
  language = "ru",
  region = "RU",
  minZoom = MIN_ZOOM,
  maxZoom,
  placemarkSize = DEFAULT_PLACEMARK_SIZE,
  placemarkHoverSize = DEFAULT_PLACEMARK_HOVER_SIZE,
  placemarkActiveSize = DEFAULT_PLACEMARK_ACTIVE_SIZE
}) => {
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: mapsKey,
    language,
    region
  });
  const markerListenersMap = useRef(/* @__PURE__ */ new Map());
  const activeMarker = useRef();
  const [map, setMap] = useState();
  const fitBounds = (map2) => {
    const bounds = new window.google.maps.LatLngBounds();
    pins.forEach((pin) => {
      bounds.extend({ lat: pin.latitude, lng: pin.longitude });
    });
    if (!pins.length) {
      return;
    }
    if (pins.length === 1) {
      map2.setCenter(bounds.getCenter());
      map2.setZoom(9);
      return;
    }
    map2.fitBounds(bounds);
  };
  const findPin = (marker) => {
    const position = marker.getPosition();
    if (!position) {
      return null;
    }
    const pin = pins.find(({ latitude, longitude }) => position.lat() === latitude && position.lng() === longitude);
    return pin || null;
  };
  const setPinIcon = (marker, iconUrl, size) => {
    marker.setIcon({
      url: iconUrl,
      scaledSize: new google.maps.Size(size[0], size[1])
    });
  };
  const setPinLabel = (marker, status) => {
    const previousLabel = marker.getLabel();
    marker.setLabel({
      ...previousLabel,
      className: status
    });
  };
  const isSelectedMarker = (marker) => {
    const label = marker.getLabel();
    return !!label && label.className === "active";
  };
  useEffect(() => {
    if (map) {
      const bounds = new window.google.maps.LatLngBounds();
      for (let i = 0; i < pins.length; i++) {
        bounds.extend(new window.google.maps.LatLng(pins[i].latitude, pins[i].longitude));
      }
      fitBounds(map);
    }
  }, [pins, map]);
  const onLoad = useCallback((map2) => {
    fitBounds(map2);
    setMap(map2);
  }, []);
  const onUnmount = useCallback((map2) => {
    setMap(void 0);
  }, []);
  const onLoadMark = (marker) => {
    const currentPin = findPin(marker);
    if (!currentPin) {
      return;
    }
    const mouseoutListener = marker.addListener("mouseout", () => {
      const isSelected = isSelectedMarker(marker);
      if (!isSelected) {
        setPinIcon(marker, placemarkImage, placemarkSize);
      } else {
        setPinIcon(marker, placemarkActiveImage, placemarkActiveSize);
      }
    });
    const mouseoverListener = marker.addListener("mouseover", () => {
      const isSelected = isSelectedMarker(marker);
      if (!isSelected) {
        setPinIcon(marker, placemarkHoverImage, placemarkHoverSize);
      } else {
        setPinIcon(marker, placemarkActiveImage, placemarkActiveSize);
      }
    });
    const clickListener = marker.addListener("click", () => {
      const isSelected = isSelectedMarker(marker);
      if (activeMarker.current) {
        setPinLabel(activeMarker.current, "default");
      }
      activeMarker.current = marker;
      onSelectPin == null ? void 0 : onSelectPin(isSelected ? currentPin.id : null);
      setPinLabel(marker, isSelected ? "default" : "active");
      setPinIcon(
        marker,
        isSelected ? placemarkImage : placemarkActiveImage,
        isSelected ? placemarkSize : placemarkActiveSize
      );
    });
    markerListenersMap.current.set(currentPin.id, [mouseoutListener, mouseoverListener, clickListener]);
  };
  const onUnmountMark = (marker) => {
    const currentPin = findPin(marker);
    if (!currentPin) {
      return;
    }
    const listeners = markerListenersMap.current.get(currentPin.id);
    listeners == null ? void 0 : listeners.forEach((listener) => listener.remove());
  };
  const onClick = useCallback(
    (e) => {
      if (!onMapClick) {
        return;
      }
      onMapClick({
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
        pageX: e.domEvent.screenX,
        pageY: e.domEvent.screenY
      });
    },
    [onMapClick]
  );
  if (!isLoaded) {
    return null;
  }
  return /* @__PURE__ */ jsx(
    GoogleMap,
    {
      mapContainerStyle: containerStyle,
      center: MAP_CENTER,
      onLoad,
      onUnmount,
      onClick,
      options: {
        minZoom,
        maxZoom
      },
      children: pins.map(({ id, latitude: lat, longitude: lng }) => {
        const isSelected = selected === id;
        return /* @__PURE__ */ jsx(
          Marker,
          {
            onLoad: onLoadMark,
            onUnmount: onUnmountMark,
            position: { lat, lng },
            icon: {
              url: isSelected ? placemarkActiveImage : placemarkImage,
              scaledSize: new google.maps.Size(placemarkSize[0], placemarkSize[1])
            },
            label: {
              text: id,
              fontSize: "0px",
              className: isSelected ? "active" : "default"
            }
          },
          id
        );
      })
    }
  );
};
const Google$1 = React__default.memo(Google);
export {
  Google$1 as default
};
//# sourceMappingURL=Google.js.map
